import React, { useState } from 'react';
import { FAQItem, Language, SiteSettings } from '../types';
import { HelpCircle, ChevronDown, MessageSquare } from 'lucide-react';

interface FaqSectionProps {
  settings: SiteSettings;
  language: Language;
  onOpenChat: () => void;
}

export const FaqSection: React.FC<FaqSectionProps> = ({ settings, language, onOpenChat }) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const getDefaults = (): FAQItem[] => [
    {
      id: 'default-1',
      question_pt: 'Como funcionam os pedidos de arte personalizada?',
      question_en: 'How do custom art orders work?',
      question_es: '¿Cómo funcionan los pedidos de arte personalizado?',
      question_ru: 'Как работают индивидуальные заказы на арт?',
      answer_pt: 'Para fazer um pedido, basta criar uma conta ou fazer o login, navegar pelas abas do site e selecionar a categoria apropriada. Você pode escolher dimensões, enviar referências de fundo, customizar textos e adicionar detalhes adicionais. O progresso pode ser acompanhado diretamente pelo seu painel de usuário.',
      answer_en: 'To place an order, simply create an account or log in, browse the categories, and request custom designs. You can specify dimensions, configure background images, add custom texts, and more. All steps of your order can be tracked in real-time in your client dashboard.',
      answer_es: 'Para realizar un pedido, simplemente crea una cuenta o inicia sesión, explora las categorías del sitio y selecciona la adecuada. Puedes elegir dimensiones, enviar referencias de fondo, personalizar textos y añadir detalles adicionales. El progreso se puede seguir directamente desde tu panel de usuario.',
      answer_ru: 'Чтобы сделать заказ, просто создайте аккаунт или войдите в систему, перейдите в нужную категорию и выберите оформление. Вы можете выбрать размеры, прикрепить фон, настроить текст и указать дополнительные детали. Отслеживать прогресс можно в реальном времени в личном кабинете.'
    },
    {
      id: 'default-2',
      question_pt: 'Quais são os formatos de entrega?',
      question_en: 'What are the delivery formats?',
      question_es: '¿Cuáles son los formatos de entrega?',
      question_ru: 'В каких форматах доставляются готовые работы?',
      answer_pt: 'Nossas artes para a Steam são otimizadas para atender perfeitamente aos padrões da plataforma. Entregamos arquivos GIF de alta qualidade para artes animadas e PNGs em alta definição para artes estáticas, além de links e instruções para o upload perfeito sem perdas de qualidade.',
      answer_en: 'Our Steam artworks are optimized to fully comply with the platform standards. We deliver high-quality GIF files for animated backgrounds and high-definition PNGs for static artworks, alongside instructions for an absolute lossless upload.',
      answer_es: 'Nuestras obras para Steam están optimizadas para cumplir perfectamente con los estándares de la plataforma. Entregamos archivos GIF de alta calidad para artes animadas y PNGs en alta definición para artes estáticas, además de enlaces e instrucciones para subirlas sin pérdida de calidad.',
      answer_ru: 'Наши арты для Steam оптимизированы в строгом соответствии со стандартами платформы. Мы предоставляем высококачественные GIF-файлы для анимаций и PNG высокого разрешения для статичных артов, а также инструкции по загрузке без потери качества.'
    },
    {
      id: 'default-3',
      question_pt: 'Quais métodos de pagamento são aceitos?',
      question_en: 'What payment methods are accepted?',
      question_es: '¿Qué métodos de pago se aceptan?',
      question_ru: 'Какие способы оплаты принимаются?',
      answer_pt: 'Oferecemos opções modernas para transações rápidas e integradas de acordo com as especificações do Santuário. Todos os detalhes de orçamento e acréscimos adicionais para customizações extras podem ser sanados através do nosso canal direto no chat ou contatos de suporte.',
      answer_en: 'We support modern payment methods to initiate projects instantly. Detailed cost estimates and additional customization fees can be comfortably negotiated or discussed in detail through our real-time messenger chat or Support.',
      answer_es: 'Ofrecemos métodos de pago modernos para transacciones rápidas e integradas según las especificaciones del Santuario. Todos los detalles de presupuesto y tarifas adicionales para personalizaciones extras pueden consultarse a través de nuestro chat directo o canales de soporte.',
      answer_ru: 'Мы поддерживаем современные и безопасные способы оплаты. Все детали сметы и стоимость дополнительных улучшений можно обсудить напрямую через наш встроенный чат или со службой поддержки.'
    }
  ];

  const faqs = (settings.faqs && settings.faqs.length > 0) ? settings.faqs : getDefaults();

  const toggle = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  const getQuestion = (faq: FAQItem) => {
    if (language === 'es') return faq.question_es || faq.question_en || faq.question_pt;
    if (language === 'ru') return faq.question_ru || faq.question_en || faq.question_pt;
    if (language === 'en') return faq.question_en || faq.question_pt;
    return faq.question_pt || faq.question_en;
  };

  const getAnswer = (faq: FAQItem) => {
    if (language === 'es') return faq.answer_es || faq.answer_en || faq.answer_pt;
    if (language === 'ru') return faq.answer_ru || faq.answer_en || faq.answer_pt;
    if (language === 'en') return faq.answer_en || faq.answer_pt;
    return faq.answer_pt || faq.answer_en;
  };

  const uiText = {
    pt: {
      badge: 'DÚVIDAS FREQUENTES',
      titlePrefix: 'CENTRAL DE',
      titleHighlight: 'SANTUÁRIO FAQ',
      desc: 'Tudo o que você precisa saber sobre as encomendas de artes Steam, formatos de arquivos, prazos e pacotes de customização.',
      ctaQuestion: 'AINDA POSSUI ALGUMA DÚVIDA?',
      ctaBtn: 'ABRIR CHAT',
      untitled: 'Pergunta Sem Título',
      noAnswer: 'Nenhuma resposta disponível ainda.'
    },
    en: {
      badge: 'GOT QUESTIONS?',
      titlePrefix: 'KNOWLEDGE',
      titleHighlight: 'SANTUÁRIO FAQ',
      desc: 'Everything you need to know about steam artwork commissions, file configurations, delivery speeds, and customization packages.',
      ctaQuestion: 'STILL HAVE UNANSWERED QUESTIONS?',
      ctaBtn: 'OPEN CHAT',
      untitled: 'Untitled Question',
      noAnswer: 'No answer provided yet.'
    },
    es: {
      badge: 'PREGUNTAS FRECUENTES',
      titlePrefix: 'CENTRO DE AYUDA',
      titleHighlight: 'SANTUÁRIO FAQ',
      desc: 'Todo lo que necesitas saber sobre encargos de arte para Steam, formatos de archivo, plazos de entrega y paquetes de personalización.',
      ctaQuestion: '¿AÚN TIENES ALGUNA DUDA?',
      ctaBtn: 'ABRIR CHAT',
      untitled: 'Pregunta Sin Título',
      noAnswer: 'No hay respuesta disponible aún.'
    },
    ru: {
      badge: 'ЧАСТЫЕ ВОПРОСЫ',
      titlePrefix: 'БАЗА ЗНАНИЙ',
      titleHighlight: 'SANTUÁRIO FAQ',
      desc: 'Всё, что вам нужно знать о заказах артов для Steam, форматах файлов, сроках выполнения и пакетах кастомизации.',
      ctaQuestion: 'ОСТАЛИСЬ ВОПРОСЫ?',
      ctaBtn: 'ОТКРЫТЬ ЧАТ',
      untitled: 'Вопрос без названия',
      noAnswer: 'Ответ пока не предоставлен.'
    }
  }[language] || {
    badge: 'GOT QUESTIONS?',
    titlePrefix: 'KNOWLEDGE',
    titleHighlight: 'SANTUÁRIO FAQ',
    desc: 'Everything you need to know about steam artwork commissions, file configurations, delivery speeds, and customization packages.',
    ctaQuestion: 'STILL HAVE UNANSWERED QUESTIONS?',
    ctaBtn: 'OPEN CHAT',
    untitled: 'Untitled Question',
    noAnswer: 'No answer provided yet.'
  };

  return (
    <section id="faq-page" className="max-w-4xl mx-auto px-6 py-20 md:py-32 animate-in fade-in duration-1000">
      
      {/* Decorative Top Badge */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <div className="w-1.5 h-1.5 rounded-full bg-[#c084fc] shadow-[0_0_8px_#c084fc]" />
        <span className="text-[10px] font-mono tracking-[0.4em] uppercase text-zinc-500 font-bold">
          {uiText.badge}
        </span>
        <div className="w-1.5 h-1.5 rounded-full bg-[#c084fc] shadow-[0_0_8px_#c084fc]" />
      </div>

      {/* Main Display Header */}
      <div className="text-center space-y-6 mb-20">
        <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight font-sans">
          {uiText.titlePrefix}{' '}
          <span className="text-[#c084fc] bg-clip-text">{uiText.titleHighlight}</span>
        </h1>
        <p className="text-xs md:text-sm text-zinc-400 font-medium max-w-xl mx-auto leading-relaxed">
          {uiText.desc}
        </p>
      </div>

      {/* Accordion List */}
      <div className="space-y-4">
        {faqs.map((faq) => {
          const question = getQuestion(faq);
          const answer = getAnswer(faq);
          const isOpen = openId === faq.id;

          return (
            <div 
              key={faq.id}
              className={`border transition-all duration-300 bg-zinc-950 ${
                isOpen 
                  ? 'border-[#c084fc]/40 shadow-[0_0_20px_rgba(192,132,252,0.05)]' 
                  : 'border-zinc-850 hover:border-zinc-800'
              }`}
            >
              <button
                onClick={() => toggle(faq.id)}
                className="w-full text-left p-6 md:p-8 flex items-center justify-between gap-6 outline-none group"
              >
                <div className="flex items-center gap-4">
                  <HelpCircle className={`w-5 h-5 flex-shrink-0 transition-colors ${isOpen ? 'text-[#c084fc]' : 'text-zinc-650 group-hover:text-zinc-400'}`} />
                  <span className={`text-xs md:text-sm font-black uppercase tracking-wider transition-colors ${isOpen ? 'text-[#c084fc]' : 'text-zinc-300 group-hover:text-white'}`}>
                    {question || uiText.untitled}
                  </span>
                </div>
                
                <div className={`p-1.5 border transition-all duration-300 ${
                  isOpen 
                    ? 'bg-[#c084fc] border-[#c084fc] text-white rotate-180' 
                    : 'bg-transparent border-zinc-800 text-zinc-500 group-hover:border-zinc-700'
                }`}>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </button>

              {/* Collapsible Answer */}
              <div className={`transition-all duration-500 overflow-hidden ${
                isOpen ? 'max-h-[500px] border-t border-white/5 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
              }`}>
                <div className="p-6 md:p-8 text-[11px] md:text-xs leading-relaxed text-zinc-400 font-medium tracking-wide prose prose-invert">
                  {answer || uiText.noAnswer}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dynamic CTA */}
      <div className="mt-20 p-8 border border-white/5 bg-[#050505] flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="text-center md:text-left">
          <h4 className="text-sm font-black text-white uppercase tracking-wider">
            {uiText.ctaQuestion}
          </h4>
        </div>
        <div className="flex flex-wrap gap-4 justify-center">
          <div 
            onClick={onOpenChat}
            className="px-5 py-3.5 bg-white text-black hover:bg-[#c084fc] hover:text-white transition-all text-[9px] font-black uppercase tracking-widest cursor-pointer border-b-2 border-zinc-300 flex items-center gap-2"
          >
            <MessageSquare className="w-3.5 h-3.5" /> {uiText.ctaBtn}
          </div>
        </div>
      </div>

    </section>
  );
};
